import { useEffect, useMemo } from 'react';
import { Card, Col, Form, Row } from 'antd';
import { useLazyGetSignedDocumentsByTemplatesQuery } from '../../api/dashboard-api';
import styles from './signed-documents-by-template.module.css';
import { Trans, useTranslation } from 'react-i18next';
import FormActions from '../../../../shared/components/form-actions/form-actions';
import { SignDocumentsFilterFormModel } from '../../models/sign-documents-filter-form-model';
import { signDocumentsByTemplateFilterFormId } from '../../constants/constants';
import { mapDateSearchParams } from '../../../sign-requests/utils/map-date-search-params';
import SignDocumentsFilterForm from '../sign-documents-filter-form/sign-documents-filter-form';
import { startOfMonth } from 'date-fns';
import Table from '../../../../shared/components/table/table';
import { ColumnsType } from 'antd/es/table';
import { TemplateSignTime } from '../../models/template-sign-time';

const SignedDocumentsByTemplate = (): JSX.Element => {
  const [getSignedDocumentsByTemplate, { data: signedDocuments, isFetching }] =
    useLazyGetSignedDocumentsByTemplatesQuery();
  const { t } = useTranslation('dashboard');
  const [form] = Form.useForm<SignDocumentsFilterFormModel>();
  const firstOfMonth = startOfMonth(new Date());
  const initialRange = useMemo(() => [firstOfMonth, new Date()], [firstOfMonth]);

  useEffect(() => {
    form.setFieldsValue({ dateFromTo: initialRange });
    getSignedDocumentsByTemplate(mapDateSearchParams(initialRange.toString()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalSum = useMemo(() => (signedDocuments || []).reduce((a, b) => a + Number(b.count), 0), [signedDocuments]);

  const filterByDate = (values: SignDocumentsFilterFormModel) => {
    const dateIntervalString = values.dateFromTo?.toString();
    getSignedDocumentsByTemplate(mapDateSearchParams(dateIntervalString || ''));
  };

  const signedDocsByTemplateDataSource = useMemo(() => {
    return (signedDocuments || []).map(({ id, name, avg, count }) => ({
      id,
      name,
      avg: Math.trunc(Math.round(Number(avg))),
      count,
    }));
  }, [signedDocuments]);

  const columns: ColumnsType<(typeof signedDocsByTemplateDataSource)[number]> = [
    {
      title: t('label.template'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('label.signatureCount'),
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => Number(a.count) - Number(b.count),
    },
    {
      title: t('label.signingTime'),
      dataIndex: 'avg',
      key: 'avg',
      sorter: (a, b) => a.avg - b.avg,
      render: value => `${value} ${t('label.seconds')}`,
    },
  ];

  return (
    <Card
      title={t('signedDocumentByTemplate.title')}
      extra={<Trans i18nKey="label.totalSigned" t={t} ns="dashboard" values={{ count: totalSum }} />}
      className="dashboard-card"
    >
      <Row gutter={16} align="middle">
        <Col>
          <SignDocumentsFilterForm form={form} onFinish={filterByDate} formId={signDocumentsByTemplateFilterFormId} />
        </Col>
        <Col>
          <FormActions
            form={form}
            formId={signDocumentsByTemplateFilterFormId}
            clearFilters={() => getSignedDocumentsByTemplate()}
          />
        </Col>
      </Row>
      <div className={styles.tableContainer}>
        <Table
          tableProps={{
            rowKey: ({ id }: TemplateSignTime) => id,
            scroll: {
              y: 58 * 10,
            },
            loading: isFetching,
            dataSource: signedDocsByTemplateDataSource,
            columns,
          }}
        />
      </div>
    </Card>
  );
};

export default SignedDocumentsByTemplate;
